import { SortingState, OnChangeFn, RowSelectionState } from '@tanstack/react-table';

// Basic interfaces
export interface SearchItem {
  id: string;
  name: string;
}

export interface DropdownOption {
  id: string;
  label: string;
}

export interface ColumnFilterProps {
  columnId: string;
  filterType: 'text' | 'number' | 'select';
  value: string;
  operator?: string;
  onValueChange: (value: string) => void;
  onOperatorChange: (operator: string) => void;
  dropdownOptions?: DropdownOption[];
}

export interface OperatorOption {
  value: string;
  label: string;
}

export interface MenuCoords {
  top: number;
  left: number;
}

export interface CustomColumnDef {
  name: string;
  header: React.ReactNode | string;
  sortable?: boolean;
  render?: (info: { data: any; value: any }) => React.ReactNode;
  minWidth?: number;
  type?: string;
  filterType?: 'text' | 'number' | 'select';
  dropdownOptions?: DropdownOption[];
}

export interface TanStackGridProps {
  columns: CustomColumnDef[];
  data: any[];
  pagination?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  enableRowSelection?: boolean;
  getRowId?: (originalRow: any, index: number) => string;
  manualPagination?: boolean;
  totalCount?: number;
  pageIndex?: number;
  onPageChange?: (pageIndex: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  manualSorting?: boolean;
  onSortingChange?: OnChangeFn<SortingState>;
  sorting?: SortingState;
  loading?: boolean;
  showFilters?: boolean;
  filterValues?: Record<string, string>;
  filterOperators?: Record<string, string>;
  onFilterChange?: (columnId: string, value: string) => void;
  onFilterOperatorChange?: (columnId: string, operator: string) => void;
  columnOrder?: string[];
  onColumnOrderChange?: (order: string[]) => void;
  columnVisibilityModel?: Record<string, boolean>;
  storageKey?: string;
}

export interface ColumnContext {
  tab: string;
  groups: string[] | undefined;
  isAuthenticated: boolean;
  isTrial: boolean;
  isAcademic: boolean;
  isGDA: boolean;
  isVDA: boolean;
  isDDA?: boolean;
  isEvidence?: boolean;
  isMapping?: boolean;
  view?: string;
}

export interface ChemicalColumnsContext {
  tab: string;
  isGDA: boolean;
  isVDA: boolean;
  isAcademic: boolean;
  isAuthenticated: boolean;
}

export interface ButtonInfoGeneContentProps {
  value: any;
}

export interface OpenPopupProps {
  popupType: string;
}

export interface FilterValue {
  name: string;
  value: string;
  operator?: string;
  type?: string;
  integer?: boolean;
  isEvidence?: boolean;
  label?: string;
  dir?: string;
  disable?: boolean;
}

export interface ColumnDef {
  name: string;
  header: React.ReactNode | string;
  sortable?: boolean;
  render?: (info: { data: any; value: any }) => React.ReactNode;
  minWidth?: number;
  type?: string;
  filterType?: 'text' | 'number' | 'select';
  dropdownOptions?: Array<{ id: string; label: string }>;
}

export interface GridProps {
  columns: any[];
  columnVisibilityModel?: Record<string, boolean>;
  columnOrder?: string[];
  setColumnOrder?: (order: string[]) => void;
  view: string;
  source: string;
  tab: string;
  panelTab?: string;
  searchItems: any[];
  isEvidence: boolean;
  isMapping?: boolean;
  setTotalResults: (count: number) => void;
  getFilterValueRef?: any;
  onData: (areItemsSelected: boolean, selectedData: any[], selectedCount?: number, selectedIds?: string[]) => void;
  sourceWeights?: any;
  defaultFilterValue?: any[];
  enableEnrichmentSelection?: boolean;
  getRowId?: (row: any, index: number) => string;
  getRowIdForEnrichment?: (row: any) => string;
  enrichmentGetSelectionRef?: React.MutableRefObject<(() => {
    selectedIds: string[];
    rowData: any[];
    getRowId: ((row: any, index: number) => string) | null;
    tab?: string;
  }) | null>;
}

export interface CacheEntry {
  data: any[];
  totalCount: number;
  timestamp: number;
}

export interface UserContext {
  isAuthenticated: boolean;
  authorization: any;
  setIsAuthenticated: (value: boolean) => void;
  groups: string[];
  setGroups: (groups: string[]) => void;
  setAuthError: (error: any) => void;
  setAuthorization: (auth: any) => void;
  restrictGridDisplay: boolean;
  restrictGridResultCount: boolean;
  restrictBrowseAll: boolean;
  restrictSources: any;
  restrictChemical: boolean;
  mappingContent: any;
  user: any;
}

export interface FetchParams {
  view: string;
  source: string;
  tab: string;
  searchItems: any[];
  isEvidence: boolean;
  isMapping?: boolean;
  mappingContent: any;
  skip: number;
  limit: number;
  orderByParam?: string;
  order?: number;
  defaultFilterValue: FilterValue[];
  sourceWeights?: any;
  isAuthenticated: boolean;
}

export interface GridState {
  rowData: any[];
  totalCount: number;
  loading: boolean;
  showPopup: boolean;
  skip: number;
  limit: number;
  orderByParam?: string;
  order?: number;
  showFiltersModal: boolean;
  URLWithFilters: string;
  showCopiedAlert: boolean;
  pendingFiltersToSave: FilterValue[] | null;
  alertMessage: string;
  filtersLoadedFromURL: boolean;
  defaultFilterValue: FilterValue[];
  incompleteFilterMessage: string | null;
  selectedRow: any[];
  isSessionExpired: boolean;
}

export interface SortModel {
  colId: string;
  sort: 'asc' | 'desc';
}

export interface ApiResponse {
  payload?: any[];
  paging?: {
    totalElements: number;
    filteredElements?: number;
    filteredCount?: number;
  };
  filteredCount?: number;
  status?: number;
}

export interface DropdownOptions {
  [key: string]: any[] | { [key: string]: any[] };
}

export interface PopupMessageConfig {
  text: string;
  linkText: string;
  linkUrl: string;
  additionalText?: string;
  additionalLinkText?: string;
  additionalLinkUrl?: string;
}

export interface PopupMessageProps {
  isAuthenticated: boolean;
  restrictBrowseAll: boolean;
  isTrial: boolean;
  onClose: () => void;
}

export interface SaveFiltersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  tab: string;
  view: string;
  source: string;
  searchItems: any[];
  pendingFiltersToSave: FilterValue[] | null;
}

export interface UseSelectionHandlingProps {
  onData: (areItemsSelected: boolean, selectedData: any[], selectedCount?: number, selectedIds?: string[]) => void;
  setSelectedRow: (data: any[]) => void;
}

export interface UseColumnManagementProps {
  columns: any[];
  tab: string;
}

export interface UseFilterSavingProps {
  pendingFiltersToSave: FilterValue[] | null;
  isEvidence: boolean;
  tab: string;
  view: string;
  source: string;
  searchItems: any[];
  user: any;
  setAlertMessage: (message: string) => void;
  setShowCopiedAlert: (show: boolean) => void;
  setShowFiltersModal: (show: boolean) => void;
}

export interface UsePaginationAndSortingProps {
  isAuthenticated: boolean;
  isTrial: boolean;
  restrictBrowseAll: boolean;
  limit: number;
  orderByParam?: string;
  order?: number;
  setSkip: (skip: number) => void;
  setLimit: (limit: number) => void;
  setOrderByParam: (param?: string) => void;
  setOrder: (order?: number) => void;
}

export interface UseDataFetchingProps {
  view: string;
  source: string;
  tab: string;
  searchItems: any[];
  isEvidence: boolean;
  isMapping?: boolean;
  skip: number;
  limit: number;
  orderByParam?: string;
  order?: number;
  defaultFilterValue: FilterValue[];
  sourceWeights?: any;
  userContext: UserContext;
  mappingContent: any;
  checkIncompleteFilters: (filters: FilterValue[]) => string | null;
  setRowData: (data: any[]) => void;
  setTotalCount: (count: number) => void;
  setTotalResults: (count: number) => void;
  setLoading: (loading: boolean) => void;
  setIsSessionExpired: (expired: boolean) => void;
  setShowPopup: (show: boolean) => void;
  setIncompleteFilterMessage: (message: string | null) => void;
  cacheRef: React.RefObject<Record<string, CacheEntry>>;
  prevDataRef: React.RefObject<{ data: any[]; count: number }>;
  fetchTimeoutRef: React.RefObject<NodeJS.Timeout | null>;
}

// Types for chemical columns
export interface ChemicalData {
  chebi?: string[];
  drugbank?: string[];
  mesh?: string[];
  pubchem?: string[];
  chemPrefName?: string;
  chemID?: string;
  chemMoleculeType?: string;
  chemNumPublications?: number;
  chemNumGDAs?: number;
  chemNumVDAs?: number;
}

export interface ChemicalColumnProps {
  data: ChemicalData;
}

export interface ChemicalRenderProps {
  data: ChemicalData;
  value?: any;
}

export const mapItemsToSearchItems = (items: any[]): { id: string; name: string }[] => {
  if (!items) return [];

  const getItemId = (item: any) => {
    const idKey = Object.keys(item).find(key => /id$/i.test(key));
    return idKey ? item[idKey] : undefined;
  };

  const nameByDataset: Record<string, string> = {
    GENES: 'symbol',
    CHEMICALS: 'chemPrefName',
    DISEASES: 'name',
    VARIANTS: 'geneSymbol',
  };

  const idByDataset: Record<string, string> = {
    GENES: 'geneID',
    CHEMICALS: 'chemID',
    DISEASES: 'diseaseID',
    VARIANTS: 'strID',
  };

  return items.map(item => {
    const name = item[nameByDataset[item.dataset]] ?? getItemId(item);
    const id = item.id ?? item.identifier ?? item.key ?? item[idByDataset[item.dataset]] ?? '';
    return { id, name };
  });
};

export type SortingChangeHandler = OnChangeFn<SortingState>;
export type RowSelectionChangeHandler = OnChangeFn<RowSelectionState>;
